import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import api from '../services/api';

interface User {
  id: string;
  accountId: string;
  email: string;
  role: string;
  emailVerified: boolean;
  notificationPrefs: Record<string, boolean>;
  preferredLanguage?: string;
}

interface Account {
  id: string;
  name: string;
  businessLegalName: string | null;
  billingCountry: string | null;
  taxId: string | null;
  plan: string;
  billingStatus: string;
  onboardingCompleted: boolean;
  onboardingStep: number;
  onboardingMode?: 'GOOGLE' | 'FACEBOOK' | 'MANUAL';
}

interface AuthState {
  user: User | null;
  account: Account | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    account: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      loadUser();
    } else {
      setState(s => ({ ...s, isLoading: false }));
    }
  }, []);

  async function loadUser() {
    try {
      const res = await api.get('/auth/me');
      setState({
        user: res.data.user,
        account: res.data.account,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setState({ user: null, account: null, isAuthenticated: false, isLoading: false });
    }
  }

  async function login(email: string, password: string) {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', res.data.accessToken);
    localStorage.setItem('refreshToken', res.data.refreshToken);
    setState({
      user: res.data.user,
      account: res.data.account,
      isAuthenticated: true,
      isLoading: false,
    });
  }

  async function signup(name: string, email: string, password: string) {
    const res = await api.post('/auth/signup', { name, email, password });
    localStorage.setItem('accessToken', res.data.accessToken);
    localStorage.setItem('refreshToken', res.data.refreshToken);
    setState({
      user: res.data.user,
      account: res.data.account,
      isAuthenticated: true,
      isLoading: false,
    });
  }

  function logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setState({ user: null, account: null, isAuthenticated: false, isLoading: false });
  }

  async function refreshUser() {
    await loadUser();
  }

  return (
    <AuthContext.Provider value={{ ...state, login, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
